import { inject, Injectable, InjectionToken } from '@angular/core';

export type LogLevel = 'info' | 'warn' | 'error' | 'off';

/**
 * The level an app logs at, provided in its app.config. Read on every call, so the staff app can
 * follow its app-config.json, which is only loaded at startup. Without a provider nothing is logged.
 */
export const LOG_LEVEL = new InjectionToken<() => LogLevel>('LOG_LEVEL', {
  factory: (): (() => LogLevel) => () => 'off',
});

const LEVEL_MAP: Record<LogLevel, LogLevel[]> = {
  error: ['error'] as const,
  info: ['info', 'warn', 'error'] as const,
  off: [] as const,
  warn: ['warn', 'error'] as const,
};

@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  readonly #logLevel = inject(LOG_LEVEL);

  #shouldLog(level: LogLevel): boolean {
    return LEVEL_MAP[this.#logLevel()]?.includes(level) ?? false;
  }

  /**
   * Maps LogLevel to the approprite method of the Console object.
   * Returns a function that prints any arguments to the console.
   */
  #getLogFunction(level: LogLevel): (params: unknown) => void {
    switch (level) {
      case 'info':
        // eslint-disable-next-line no-console
        return console.log;
      case 'warn':
        // eslint-disable-next-line no-console
        return console.warn;
      case 'error':
        // eslint-disable-next-line no-console
        return console.error;
      default:
        return () => {
          // do nothing
        };
    }
  }

  #log(level: LogLevel, message: string, ...params: unknown[]): void {
    if (!this.#shouldLog(level)) {
      return;
    }
    if (params.length) {
      // eslint-disable-next-line no-console
      console.group(message);
      params.forEach(param => this.#getLogFunction(level)(param));
      // eslint-disable-next-line no-console
      console.groupEnd();
    } else {
      this.#getLogFunction(level)(message);
    }
  }

  log(message: string, ...params: unknown[]): void {
    this.#log('info', message, ...params);
  }

  info(message: string, ...params: unknown[]): void {
    this.#log('info', message, ...params);
  }

  warn(message: string, ...params: unknown[]): void {
    this.#log('warn', message, ...params);
  }

  debug(message: string, ...params: unknown[]): void {
    this.#log('warn', message, ...params);
  }

  error(message: string, ...params: unknown[]): void {
    this.#log('error', message, ...params);
  }
}
